import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TicketsApiService } from '@simOn/ticket/element/data-access';
import { Ticket } from '@simOn/ticket/element/models';
import { isNewTicket } from '@simOn/ticket/models';
import {
  EMPTY,
  Subject,
  catchError,
  concat,
  distinctUntilChanged,
  exhaustMap,
  filter,
  iif,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
  withLatestFrom
} from 'rxjs';
import {
  AddTicket,
  AddTicketComment,
  AddTicketCommentSuccess,
  AddTicketFailure,
  AddTicketSuccess,
  ClearTicketState,
  DeleteTicket,
  DeleteTicketComment,
  DeleteTicketCommentFailure,
  DeleteTicketCommentSuccess,
  DeleteTicketFailure,
  DeleteTicketSuccess,
  DeleteUploadedFileSimplified,
  DeleteUploadedFileSimplifiedFailure,
  DeleteUploadedFileSimplifiedSuccess,
  GetTicket,
  GetTicketFailure,
  GetTicketSuccess,
  GetTickets,
  GetTicketsFailure,
  GetTicketsSuccess,
  HandleTicketModified,
  HandleTicketModifiedSuccess,
  MarkTicketAsRead,
  MarkTicketAsReadSuccess,
  SetSelectedId,
  UpdateStatus,
  UpdateStatusFailure,
  UpdateStatusSuccess,
  UpdateTicket,
  UpdateTicketComment,
  UpdateTicketCommentFailure,
  UpdateTicketCommentSuccess,
  UpdateTicketFailure,
  UpdateTicketSuccess
} from './ticket.actions';
import { TicketFacade } from './ticket.facade';
import { GetSelectedTicket } from './ticket.selectors';

@Injectable()
export class TicketEffects {
  private readonly actions$ = inject(Actions);
  private readonly ticketsApiService = inject(TicketsApiService);
  private readonly ticketFacade = inject(TicketFacade);
  private readonly store = inject(Store);
  private readonly _destroySource: Subject<void> = new Subject<void>();

  getTicket$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(GetTicket),
        mergeMap((result) => this.ticketsApiService.getTicket$(result.state))
      )
      .pipe(
        map((response) => GetTicketSuccess({ state: response })),
        catchError((error) => of(GetTicketFailure(error)))
      )
  );

  markTicketAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkTicketAsRead),
      mergeMap((result) =>
        this.ticketsApiService.markTicketAsRead$(result.ticketId).pipe(
          map(() => MarkTicketAsReadSuccess({ ticketId: result.ticketId })),
          catchError((error) => of(GetTicketFailure(error)))
        )
      )
    )
  );

  getTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetTickets),
      tap(() => this._destroySource.next()),
      mergeMap((action) => {
        if (action.autoUpdate) {
          //TODO: (olek) sprawdzić zalogowanie
          return timer(0, 30000).pipe(
            switchMap(() => this.ticketFacade.preventRefresh$.pipe(take(1))),
            filter((preventRefresh) => preventRefresh === false),
            switchMap(() => this.ticketsApiService.getTickets$()),
            takeUntil(this._destroySource)
          );
        } else {
          return this.ticketsApiService.getTickets$();
        }
      }),
      filter((tickets): tickets is Ticket[] => !!tickets),
      map((response) => GetTicketsSuccess({ state: response })),
      catchError((error) => of(GetTicketsFailure(error)))
    )
  );

  addTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTicket),
      exhaustMap((result) =>
        this.ticketsApiService.createTicket$(result.state).pipe(
          mergeMap((response) => concat([AddTicketSuccess(), SetSelectedId({ selectedId: response })])),
          catchError((error) =>
            of(
              AddTicketFailure({
                state: {
                  message: error,
                  name: 'error'
                }
              })
            )
          )
        )
      )
    )
  );

  updateTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateTicket),
      mergeMap((result) =>
        iif(
          () => isNewTicket(result.state.ticketId),
          of(result.state).pipe(map(() => UpdateTicketSuccess())),
          of(true).pipe(
            switchMap(() => this.ticketsApiService.updateTicket$(result.state)),
            switchMap(() => concat([UpdateTicketSuccess(), GetTicket({ state: result.state.ticketId! })])),
            catchError((error) => {
              return of(
                UpdateTicketFailure({
                  state: {
                    message: error,
                    name: 'error'
                  }
                })
              );
            })
          )
        )
      )
    )
  );

  handleTicketModified$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandleTicketModified),
      mergeMap((result) => this.ticketsApiService.getTicket$(result.ticketId)),
      switchMap((response) =>
        concat([GetTicketSuccess({ state: response }), HandleTicketModifiedSuccess({ ticket: response })])
      )
    )
  );

  deleteTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteTicket),
      exhaustMap((result) =>
        this.ticketsApiService.deleteTicket$(result.state.ticketId, result.state.name).pipe(
          map(() => result),
          map((response) => DeleteTicketSuccess(response)),
          catchError((error) => of(DeleteTicketFailure(error)))
        )
      )
    )
  );

  addTicketComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTicketComment),
      mergeMap((result) =>
        this.ticketsApiService.addTicketComment$(result.state.ticketComment).pipe(
          map((ticketComment) => ({
            result,
            ticketComment
          }))
        )
      ),
      map((response) => {
        return AddTicketCommentSuccess({
          state: { ticketId: response.result.state.ticketId, ticketComment: response.ticketComment }
        });
      }),
      catchError((error) => of(AddTicketCommentSuccess(error)))
    )
  );

  updateTicketComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateTicketComment),
      mergeMap((result) =>
        this.ticketsApiService.updateTicketComment$(result.state.ticketComment).pipe(
          map(() => result),
          map((response) => UpdateTicketCommentSuccess(response)),
          catchError((error) => of(UpdateTicketCommentFailure(error)))
        )
      )
    )
  );

  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStatus),
      mergeMap((result) =>
        this.ticketsApiService.updateTicketStatus$(result.state).pipe(
          mergeMap(() =>
            concat([UpdateStatusSuccess({ state: result.state }), GetTicket({ state: result.state.ticketId })])
          ),
          catchError((error) => of(UpdateStatusFailure(error)))
        )
      )
    )
  );

  deleteTicketComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteTicketComment),
      exhaustMap((result) => this.ticketsApiService.deleteTicketComment$(result.state).pipe(map(() => result))),
      switchMap((result) =>
        this.ticketFacade.selectedTicketId$.pipe(
          map((id: string | undefined) => {
            this.ticketFacade.getTicket(id!);
            return result;
          })
        )
      ),
      map((response) => DeleteTicketCommentSuccess(response)),
      catchError((error) => of(DeleteTicketCommentFailure(error)))
    )
  );

  deleteUploadedFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteUploadedFileSimplified),
      withLatestFrom(this.store.select(GetSelectedTicket)),
      exhaustMap(([result, ticket]) =>
        this.ticketsApiService
          .deleteAttachmentsFromTicket$(ticket!.id!, [result.state.fileGuid])
          .pipe(map(() => ticket!.id))
      ),
      mergeMap((ticketId) => of(GetTicket({ state: ticketId! }), DeleteUploadedFileSimplifiedSuccess())),
      catchError((error) => of(DeleteUploadedFileSimplifiedFailure(error)))
    )
  );

  selectedTicketId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetSelectedId),
      distinctUntilChanged((previous, current) => previous.selectedId === current.selectedId),
      mergeMap(({ selectedId }) => {
        if (!!selectedId) return of(GetTicket({ state: selectedId }));
        return EMPTY;
      })
    )
  );

  clearState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClearTicketState),
        tap(() => this._destroySource.next())
      ),
    { dispatch: false }
  );
}
